import React, { useEffect, useMemo } from 'react'
import FormLayout from 'gms-support/src/core/page/layout/FormLayout'
import { DropdownItemProps, Grid, GridRow, Segment } from 'semantic-ui-react'
import { isRequired } from '@cbgms/base/utils/validations'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { getDistributors } from 'app-support/licenses/state/actions'
import { listShopByCompanyUUID } from 'app-support/shops/state/actions'
import { IRootReducerState } from 'state/reducers'
import Dropdown from '@cbgms/components/form/Dropdown'
import { formValueSelector } from 'redux-form'
import OptionalLicenseFields from './OptionalLicenseFields'
import { IDistributor, IShopByCompany } from '@cbgms/api'
import { useParams } from 'react-router-dom'
import { FOR_ALL_SHOPS_IN_COMPANY } from 'app-support/company/components/Licenses/ShopIDHelper'
import { useQueryParam } from '@cbgms/base/hooks/use-query-param/use-query-param'
import { QueryParamAllShops } from './HaynesUtils'

export interface IFormValues {
  DistributorUUID: string
  ShopID: string
  HaynesProLicenseSettings?: {
    Username: string
    Password: string
    ClientID: string
  }
  'HaynesProLicenseSettings.ClientID': string
  ValidFrom: string
  ValidTill: string
}

interface ICompanyLicenseFormProps {
  form: string
  license?: IFormValues
  redirectToURL?: string
  handleSubmit: (values: IFormValues) => any
  onSubmitAndDone?: (values: IFormValues) => any
  onSubmitSuccess: () => any
}

const LicenseForm: React.FC<ICompanyLicenseFormProps> = ({
  form,
  license = {},
  handleSubmit,
  onSubmitSuccess,
  onSubmitAndDone,
  redirectToURL
}) => {
  const { t } = useTranslation()
  const { companyUUID } = useParams<{ companyUUID?: string }>()
  const [, allShops] = useQueryParam(QueryParamAllShops.key)

  const dispatch = useDispatch()
  const distributors: IDistributor[] = useSelector((state: IRootReducerState) => state.distributors.map(a => a.Distributor))
  const distributorUUID = useSelector((state: IRootReducerState) => formValueSelector(form)(state, 'DistributorUUID'))
  const shopsForCompany: IShopByCompany[] = useSelector((state: IRootReducerState) => state?.shops)
  const selectedShop = useSelector((state: IRootReducerState) => state?.shops?.find(s => s.ID === formValueSelector(form)(state, 'ShopID')))
  const companyCountry = selectedShop
    ? selectedShop.ID.substring(0, 2)
    : shopsForCompany.length
    ? shopsForCompany[0].ID.substring(0, 2)
    : ''

  useEffect(() => {
    const asyncFunc = async () => {
      await Promise.all([dispatch(getDistributors({ fields: '*' })), dispatch(listShopByCompanyUUID(companyUUID || ''))])
    }
    asyncFunc()
  }, [])

  const distributorOptions: DropdownItemProps[] = useMemo(
    () => distributors.map(d => ({ text: `${d.Name} (${t(`Enum.EnumLicenseType_${d.LicenseType}`)})`, value: d.UUID })),
    [distributors]
  )
  const shopIDOptions: DropdownItemProps[] = useMemo(
    () => [
      { text: `${t(`companyLicense.company_wide_license`)}`, value: FOR_ALL_SHOPS_IN_COMPANY },
      ...shopsForCompany.map((shop: IShopByCompany) => ({ text: `${shop.Name} (${shop.ID})`, value: shop.ID }))
    ],
    [shopsForCompany]
  )

  const distributor = useMemo(() => distributors.find(a => a.UUID === distributorUUID), [distributors, distributorUUID])

  const initialValues = useMemo(
    () =>
      (license && {
        DistributorUUID: license.DistributorUUID,
        ShopID: license.ShopID || FOR_ALL_SHOPS_IN_COMPANY,
        HaynesProLicenseSettings: license.HaynesProLicenseSettings,
        ValidFrom: license.ValidFrom,
        ValidTill: license.ValidTill,
        HaynesProCustomer: { Languages: [], ProductTypes: [] },
        'HaynesProLicenseSettings.ClientID': license['HaynesProLicenseSettings.ClientID']
      }) || { ShopID: FOR_ALL_SHOPS_IN_COMPANY, HaynesProCustomer: { Languages: [], ProductTypes: [] } },
    [license]
  )

  return (
    <FormLayout
      form={form}
      onSubmit={handleSubmit}
      initialValues={initialValues}
      onSubmitAndDone={onSubmitAndDone}
      onSubmitSuccess={onSubmitSuccess}
      redirectToURL={redirectToURL}
    >
      <Segment>
        <Grid stackable padded>
          <Grid.Column width='8'>
            <Grid>
              <GridRow>
                <Dropdown
                  data-testid='dropdown-licensetype'
                  validate={isRequired}
                  required
                  label={t('companyLicense.licensetype')}
                  name='DistributorUUID'
                  options={distributorOptions}
                  selection
                />
              </GridRow>
              {!allShops && (
                <GridRow>
                  <Dropdown
                    data-testid='dropdown-licenseShopID'
                    validate={isRequired}
                    required
                    label={t('companyLicense.optional_shopid')}
                    name='ShopID'
                    options={shopIDOptions}
                    selection
                  />
                </GridRow>
              )}
              {distributor && (
                <OptionalLicenseFields
                  distributor={distributor}
                  form={form}
                  companyCountry={companyCountry}
                  companyUUID={companyUUID || ''}
                  shop={selectedShop}
                />
              )}
            </Grid>
          </Grid.Column>
        </Grid>
      </Segment>
    </FormLayout>
  )
}

export default LicenseForm
